// Use relative paths for API calls - Next.js rewrites handle the proxy to backend
// This allows the same code to work both locally and in Docker
const API_URL = process.env.NEXT_PUBLIC_API_URL || '';

function getLocale(): string {
    if (typeof document !== 'undefined') {
        const match = document.cookie.split('; ').find(row => row.startsWith('NEXT_LOCALE='));
        return match ? match.split('=')[1] : 'en';
    }
    return 'en';
}

interface ApiOptions extends RequestInit {
    skipAuth?: boolean;
}

// Types
export interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    professional_area: string;
    country: string;
    city: string;
    affiliation: string | null;
    phone_country_code: string | null;
    phone_number: string | null;
    bio: string | null;
    social_links: Record<string, string> | null;
    auditor_certificate_path: string | null;
    profile_picture_path: string | null;
    is_verified: boolean;
    is_active: boolean;
    role: string;
    created_at: string;
    updated_at: string;
}

export interface Notification {
    id: string;
    title: string;
    message: string;
    notification_type: 'info' | 'success' | 'warning';
    is_read: boolean;
    link: string | null;
    created_at: string;
}

export interface AuditorApplication {
    id: string;
    user_id: string;
    user_name: string;
    user_email: string;
    user_bio: string | null;
    user_certificate_url: string | null;
    status: 'pending' | 'approved' | 'rejected';
    reviewed_by: string | null;
    reviewed_at: string | null;
    created_at: string;
}

export interface MyApplication {
    id: string;
    user_bio: string | null;
    user_certificate_url: string | null;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string | null;
}

export interface PendingUser {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    professional_area: string;
    country: string;
    city: string;
    affiliation: string | null;
    phone_number: string | null;
    created_at: string;
}

// Auditor types
export interface AuditorListItem {
    id: string;
    first_name: string;
    last_name: string;
    city: string;
    country: string;
}

export interface AuditorDetails {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    city: string;
    country: string;
    professional_area: string;
    affiliation: string | null;
    phone_country_code: string | null;
    phone_number: string | null;
}

export interface Country {
    code: string;
    name_en: string;
    name_pt: string;
}

// Certification types
export interface LocationItem {
    id: string;
    name?: string;
    locationType?: 'site' | 'offsite';
    color?: string;
    latitude: string;
    longitude: string;
    address: string;
    suburb: string;
    parish: string;
    postalCode: string;
    city: string;
    region: string;
    country: string;
}

export interface Certification {
    id: string;
    user_id: string;
    status: 'draft' | 'submitted' | 'in_review' | 'certified' | 'rejected';
    current_stage: number;
    name: string | null;
    description: string | null;
    weight_profile_id: string | null;
    modularity_type: string | null;
    transport_approach: string | null;
    assembly_approach: string | null;
    structural_material: string | null;
    structural_system: string | null;
    // Site Location
    site_latitude: number | null;
    site_longitude: number | null;
    site_address: string | null;
    site_suburb: string | null;
    site_parish: string | null;
    site_postal_code: string | null;
    site_city: string | null;
    site_region: string | null;
    site_country: string | null;
    // Offsite Location
    offsite_latitude: number | null;
    offsite_longitude: number | null;
    offsite_address: string | null;
    offsite_suburb: string | null;
    offsite_parish: string | null;
    offsite_postal_code: string | null;
    offsite_city: string | null;
    offsite_region: string | null;
    offsite_country: string | null;
    created_at: string;
    updated_at: string;
    submitted_at: string | null;
    assigned_auditor_id: string | null;
    assigned_at: string | null;
    locations?: LocationItem[] | null;
    comparison_colors?: Record<string, string> | null;
    files: CertificationFile[];
}

export interface CertificationFile {
    id: string;
    file_name: string;
    original_filename?: string; // Original filename before storage
    file_type: 'image' | 'document';
    content_type: string;
    file_size: number;
    uploaded_at: string;
    download_url: string | null;
    main?: boolean; // Whether this image is the main/featured image
}

export interface CertificationListItem {
    id: string;
    name: string | null;
    status: string;
    current_stage: number;
    weight_profile_id: string | null;
    site_city: string | null;
    site_country: string | null;
    offsite_city: string | null;
    offsite_country: string | null;
    assigned_auditor_id: string | null;
    assigned_auditor_name: string | null;
    creator_id?: string;
    creator_name?: string;
    assigned_at: string | null;
    updated_at: string;
    created_at: string;
    main_image_url: string | null;
}

export interface DropdownOptionItem {
    value: string;
    label_en: string;
    label_pt: string;
    order_index: number;
}

export interface FieldLabel {
    label_en: string;
    label_pt: string;
}

export interface DropdownOptions {
    modularity_types: DropdownOptionItem[];
    transport_approaches: DropdownOptionItem[];
    assembly_approaches: DropdownOptionItem[];
    structural_materials: DropdownOptionItem[];
    structural_systems: DropdownOptionItem[];
    field_labels: Record<string, FieldLabel>;
}

export interface WeightProfileListItem {
    id: string;
    name_en: string;
    name_pt: string;
    is_default: boolean;
}

export interface WeightStructureNode {
    id: string;
    key: string;
    level: 'cluster' | 'category' | 'subcategory';
    name_en: string;
    name_pt: string;
    order: number;
    weighting: number;
    global_weight: number;
    children: WeightStructureNode[];
}

export interface WeightProfileResponse {
    id: string;
    name_en: string;
    name_pt: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
    weight_tree: WeightStructureNode[];
}

export interface EmailTemplate {
    key: string;
    subject_en: string;
    subject_pt: string;
    body_en: string;
    body_pt: string;
    updated_at: string;
}

export interface GeocodingResult {
    address: string | null;
    suburb: string | null;
    parish: string | null;
    postal_code: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
}

export interface GeocodingSearchResult {
    display_name: string;
    latitude: number;
    longitude: number;
    address: string | null;
    suburb: string | null;
    parish: string | null;
    postal_code: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
}

// Scoring types
export interface CertificationScore {
    id: string;
    cluster: string;
    category_name: string;
    subcategory_name: string;
    score: number | null;
    reliability: number;
    raw_value: string | null;
    effective_score: number | null;
    created_at: string;
    updated_at: string;
}

export interface ScoreItem {
    cluster: string;
    category_name: string;
    subcategory_name: string;
    score: number | null;
    reliability: number;
    raw_value?: string | null;
}

export interface ScoreListResponse {
    certification_id: string;
    scores: CertificationScore[];
    total_count: number;
    scored_count: number;
}

// Category JSON structure types
export interface CategorySubcategory {
    name: string;
    indicator: string;
    unit: string;
    scores: Record<string, string>;
}

export interface CategoryData {
    category: string;
    info?: string;
    subcategories: CategorySubcategory[];
}

export interface GlossaryEntry {
    cluster: string;
    terminology: string;
    definition: string;
}

export interface WeightCluster {
    cluster: string;
    weighting: number;
    categories: {
        name: string;
        weighting: number;
        subcategories: {
            name: string;
            weighting: number;
        }[];
    }[];
}

// Score statistics types
export interface ScoreStatistics {
    mean: number;
    std_dev: number;
    count: number;
    distribution: number[];
}

export interface AllStatsResponse {
    subcategories: Record<string, ScoreStatistics>;
    categories: Record<string, ScoreStatistics>;
    clusters: Record<string, ScoreStatistics>;
    overall: ScoreStatistics;
}

// News types
export interface News {
    id: string;
    title: string;
    slug: string;
    description: string;
    content: string | null;
    image_url: string | null;
    published_at: string;
    created_at: string;
    updated_at: string;
    created_by_id: string;
}

export interface HeroLogo {
    id: number;
    order: number;
    image_path: string;
}

export interface HeroLogoConfig {
    alignment: 'left' | 'center' | 'right';
    logo_height: number;
}

export interface SiteConfig {
    header_logo_path: string | null;
    header_logo_size: 'small' | 'medium' | 'large';
    header_text: string | null;
    favicon_path: string | null;
    footer_address: string | null;
    footer_phone: string | null;
    footer_email: string | null;
}

export interface HeroSlide {
    id: number;
    order: number;
    image_path: string | null;
    text_en: string | null;
    text_pt: string | null;
    text_position: 'left' | 'right';
    duration_seconds: number;
}

export interface HeroSlideCreate {
    order?: number;
    image_path?: string | null;
    text_en?: string | null;
    text_pt?: string | null;
    text_position?: 'left' | 'right';
    duration_seconds?: number;
}

export interface HeroSlideUpdate {
    order?: number;
    image_path?: string | null;
    text_en?: string | null;
    text_pt?: string | null;
    text_position?: 'left' | 'right';
    duration_seconds?: number;
}

export interface NewsCreate {
    title: string;
    description: string;
    content?: string;
    image_url?: string;
}

export interface NewsUpdate {
    title?: string;
    description?: string;
    content?: string;
    image_url?: string;
}


export interface AdminSubcategoryData {
    key: string;
    name_en: string;
    name_pt: string;
    info_en: string;
    info_pt: string;
    unit_en: string;
    unit_pt: string;
    scores_en: Record<string, string>;
    scores_pt: Record<string, string>;
}

export interface AdminScoreCategory {
    key: string;
    name_en: string;
    name_pt: string;
    info_en: string;
    info_pt: string;
    subcategories: AdminSubcategoryData[];
}

export interface AdminStructureUpdate {
    key: string;
    name_en?: string;
    name_pt?: string;
    info_en?: string;
    info_pt?: string;
    unit_en?: string;
    unit_pt?: string;
    scores_en?: Record<string, string>;
    scores_pt?: Record<string, string>;
}

// Backwards compat alias
export type AdminSubcategoryUpdate = AdminStructureUpdate;

export class ApiError extends Error {
    public code?: string;
    public params?: Record<string, any>;

    constructor(message: string, code?: string, params?: Record<string, any>) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.params = params;
    }
}

class ApiClient {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
        const { skipAuth = false, ...fetchOptions } = options;

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...fetchOptions,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': getLocale(),
                ...fetchOptions.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
            const detail = error.detail;

            if (typeof detail === 'object' && detail !== null && detail.code) {
                throw new ApiError(detail.code, detail.code, detail.params);
            }

            throw new Error(detail || `HTTP error! status: ${response.status}`);
        }

        // Handle empty responses (e.g., HTTP 204 No Content)
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            return undefined as T;
        }

        return response.json();
    }

    // Auth endpoints
    async register(data: {
        first_name: string;
        last_name: string;
        email: string;
        password: string;
        professional_area: string;
        country: string;
        city: string;
        affiliation?: string;
        phone_country_code?: string;
        phone_number?: string;
    }) {
        return this.request<{ message: string; user_id: string }>('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    

    async login(email: string, password: string, rememberMe: boolean = false) {
        return this.request<{ message: string; user_id: string }>('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password, remember_me: rememberMe }),
        });
    }

    async logout() {
        return this.request<{ message: string }>('/api/auth/logout', {
            method: 'POST',
        });
    }

    async requestPasswordReset(email: string) {
        return this.request<{ message: string }>('/api/auth/request-reset', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async resetPassword(token: string, newPassword: string) {
        return this.request<{ message: string }>('/api/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, new_password: newPassword }),
        });
    }

    // User endpoints
    async getCurrentUser() {
        return this.request<User>('/api/users/me');
    }

    async updateProfile(data: {
        first_name?: string;
        last_name?: string;
        professional_area?: string;
        country?: string;
        city?: string;
        affiliation?: string;
        phone_country_code?: string;
        phone_number?: string;
        bio?: string;
        social_links?: Record<string, string>;
    }) {
        return this.request<User>('/api/users/me', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async uploadCertificate(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/users/me/certificate`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<User>;
    }

    async deleteCertificate() {
        return this.request<{ message: string }>('/api/users/me/certificate', {
            method: 'DELETE',
        });
    }

    async downloadCertificate(lastName: string) {
        const response = await fetch(`${this.baseUrl}/api/users/me/certificate`, {
            credentials: 'include',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Download failed' }));
            throw new Error(error.detail || 'Download failed');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    }

    async uploadProfilePicture(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/users/me/profile-picture`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<User>;
    }

    async deleteProfilePicture() {
        return this.request<{ message: string }>('/api/users/me/profile-picture', {
            method: 'DELETE',
        });
    }

    getProfilePictureUrl(userId?: string) {
        if (userId) {
            return `${this.baseUrl}/api/users/${userId}/profile-picture`;
        }
        return `${this.baseUrl}/api/users/me/profile-picture`;
    }

    // Notification endpoints
    async getNotifications(limit: number = 50, unreadOnly: boolean = false) {
        const params = new URLSearchParams({ limit: String(limit) });
        if (unreadOnly) params.append('unread_only', 'true');
        return this.request<Notification[]>(`/api/notifications?${params}`);
    }

    async getNotificationCount() {
        return this.request<{ unread_count: number }>('/api/notifications/count');
    }

    async markNotificationRead(notificationId: string) {
        return this.request<{ message: string }>(`/api/notifications/${notificationId}/read`, {
            method: 'POST',
        });
    }

    async markAllNotificationsRead() {
        return this.request<{ message: string }>('/api/notifications/read-all', {
            method: 'POST',
        });
    }

    // Auditor application endpoints
    async applyForAuditor() {
        return this.request<MyApplication>('/api/auditor/apply', {
            method: 'POST',
            body: JSON.stringify({}),
        });
    }

    async getMyApplication() {
        return this.request<MyApplication | null>('/api/auditor/my-application');
    }

    async deleteMyApplication() {
        return this.request<{ message: string }>('/api/auditor/my-application', {
            method: 'DELETE',
        });
    }

    async getApplications(status: 'all' | 'pending' | 'completed' = 'all') {
        return this.request<AuditorApplication[]>(`/api/auditor/applications?status=${status}`);
    }

    // Keep old method for backwards compatibility
    async getPendingApplications() {
        return this.getApplications('pending');
    }

    async getApplication(applicationId: string) {
        return this.request<AuditorApplication>(`/api/auditor/applications/${applicationId}`);
    }

    async reviewApplication(applicationId: string, decision: 'approve' | 'reject') {
        return this.request<{ message: string }>(`/api/auditor/applications/${applicationId}/review`, {
            method: 'POST',
            body: JSON.stringify({ decision }),
        });
    }

    // Auditor listing endpoints
    async getAuditors() {
        return this.request<AuditorListItem[]>('/api/auditor/list');
    }

    async getAuditorDetails(auditorId: string) {
        return this.request<AuditorDetails>(`/api/auditor/${auditorId}`);
    }

    async assignAuditor(certificationId: string, auditorId: string) {
        return this.request<Certification>(`/api/certifications/${certificationId}/assign-auditor`, {
            method: 'POST',
            body: JSON.stringify({ auditor_id: auditorId }),
        });
    }

    async unassignAuditor(certificationId: string) {
        return this.request<Certification>(`/api/certifications/${certificationId}/unassign-auditor`, {
            method: 'DELETE',
        });
    }

    // Admin endpoints
    async getUsers() {
        return this.request<(User & { role: string })[]>('/api/admin/users');
    }

    async getPendingUsersCount() {
        return this.request<{ count: number }>('/api/admin/users/pending-count');
    }

    async getUser(userId: string) {
        return this.request<User & { role: string }>(`/api/admin/users/${userId}`);
    }

    async deleteUser(userId: string) {
        return this.request<{ message: string }>(`/api/admin/users/${userId}`, {
            method: 'DELETE',
        });
    }

    async updateUserRole(userId: string, role: 'user' | 'auditor') {
        return this.request<{ message: string }>(`/api/admin/users/${userId}/role?role=${role}`, {
            method: 'PATCH',
        });
    }

    async toggleUserStatus(userId: string) {
        return this.request<{ message: string }>(`/api/admin/users/${userId}/toggle-status`, {
            method: 'POST',
        });
    }

    // User approval endpoints
    async getPendingUsers() {
        return this.request<PendingUser[]>('/api/auth/pending-users');
    }

    async approveUser(userId: string) {
        return this.request<{ message: string }>(`/api/auth/users/${userId}/approve`, {
            method: 'POST',
        });
    }

    async rejectUser(userId: string) {
        return this.request<{ message: string }>(`/api/auth/users/${userId}/reject`, {
            method: 'POST',
        });
    }

    // Data endpoints
    async getProfessionalAreas() {
        return this.request<Array<{ id: string; name_en: string; name_pt: string }>>(
            '/api/data/professional-areas'
        );
    }

    async getCountries() {
        return this.request<Array<{ code: string; name_en: string; name_pt: string }>>(
            '/api/data/countries'
        );
    }

    async healthCheck() {
        return this.request<{ status: string; app: string }>('/api/health');
    }

    // Certification endpoints
    async getCertificationOptions() {
        return this.request<DropdownOptions>('/api/certifications/options');
    }

    async createCertification(data: { name?: string; description?: string }) {
        return this.request<Certification>('/api/certifications', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getCertifications() {
        return this.request<CertificationListItem[]>('/api/certifications');
    }

    async getPublicCertifications() {
        return this.request<CertificationListItem[]>('/api/certifications/public');
    }

    async getCertification(id: string) {
        return this.request<Certification>(`/api/certifications/${id}`);
    }

    async updateCertification(id: string, data: Partial<Certification>) {
        return this.request<Certification>(`/api/certifications/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteCertification(id: string) {
        return this.request<{ message: string }>(`/api/certifications/${id}`, {
            method: 'DELETE',
        });
    }

    async uploadCertificationFile(certificationId: string, file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/certifications/${certificationId}/files`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<CertificationFile>;
    }

    async deleteCertificationFile(certificationId: string, fileId: string) {
        return this.request<{ message: string }>(`/api/certifications/${certificationId}/files/${fileId}`, {
            method: 'DELETE',
        });
    }

    async setMainImage(certificationId: string, fileId: string) {
        return this.request<CertificationFile>(`/api/certifications/${certificationId}/files/${fileId}/main`, {
            method: 'PATCH',
        });
    }

    // Scoring endpoints
    async getCertificationScores(certificationId: string) {
        return this.request<ScoreListResponse>(`/api/certifications/${certificationId}/scores`);
    }

    async updateCertificationScores(certificationId: string, scores: ScoreItem[]) {
        return this.request<ScoreListResponse>(`/api/certifications/${certificationId}/scores`, {
            method: 'PATCH',
            body: JSON.stringify({ scores }),
        });
    }

    async downloadCertificationPdf(certificationId: string, lang: 'en' | 'pt' = 'en', comparisons: string[] = [], colors: string[] = [], mapLayer: string = 'street') {
        const params = new URLSearchParams({ lang });
        if (comparisons.length) params.set('comparisons', comparisons.join(','));
        const colorValues = colors.map(c => c.replace(/^#/, '')).filter(Boolean);
        if (colorValues.length) params.set('colors', colorValues.join(','));
        if (mapLayer && mapLayer !== 'street') params.set('mapLayer', mapLayer);
        const response = await fetch(`${this.baseUrl}/api/certifications/${certificationId}/pdf?${params}`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Download failed' }));
            throw new Error(error.detail || 'Download failed');
        }

        return response.blob();
    }

    async downloadComparisonPdf(ids: string[], lang: 'en' | 'pt' = 'en', colors: string[] = []) {
        const params = new URLSearchParams({ lang });
        ids.forEach(id => params.append('ids', id));
        // Strip '#' so colors are safe as a plain URL param value
        const colorValues = colors.map(c => c.replace(/^#/, '')).filter(Boolean);
        if (colorValues.length) params.set('colors', colorValues.join(','));
        const response = await fetch(`${this.baseUrl}/api/certifications/compare/pdf?${params}`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Download failed' }));
            throw new Error(error.detail || 'Download failed');
        }

        return response.blob();
    }

    async reverseGeocode(lat: number, lng: number) {
        return this.request<GeocodingResult>(`/api/certifications/geocoding/reverse?lat=${lat}&lng=${lng}`);
    }

    async searchAddress(query: string, limit: number = 5) {
        return this.request<GeocodingSearchResult[]>(`/api/certifications/geocoding/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    }

    // Weight Profiles
    async getWeightProfiles() {
        return this.request<WeightProfileListItem[]>('/api/weight-profiles');
    }

    async getWeightProfile(id: string) {
        return this.request<WeightProfileResponse>(`/api/weight-profiles/${id}`);
    }

    async seedWeightProfiles() {
        return this.request<{ structure_seeded: boolean; default_profile_created: boolean }>('/api/weight-profiles/seed', {
            method: 'POST',
        });
    }

    async createWeightProfile(data: { name_en: string; name_pt: string }) {
        return this.request<WeightProfileResponse>('/api/weight-profiles', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateWeightProfile(id: string, data: { name_en?: string; name_pt?: string; weights?: { structure_id: string; weighting: number }[] }) {
        return this.request<WeightProfileResponse>(`/api/weight-profiles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteWeightProfile(id: string) {
        return this.request<void>(`/api/weight-profiles/${id}`, {
            method: 'DELETE',
        });
    }

    async reseedWeightProfiles() {
        return this.request<{ reseeded: boolean }>('/api/weight-profiles/reseed', {
            method: 'POST',
        });
    }

    // Statistics endpoints
    async getAllScoreStatistics(weightProfileId?: string) {
        const params = weightProfileId ? `?weight_profile_id=${weightProfileId}` : '';
        return this.request<AllStatsResponse>(`/api/statistics/all${params}`);
    }


    // News endpoints
    async getNews(limit: number = 20, offset: number = 0) {
        return this.request<News[]>(`/api/news?limit=${limit}&offset=${offset}`);
    }

    async getNewsBySlug(slug: string) {
        return this.request<News>(`/api/news/${slug}`);
    }

    async createNews(data: NewsCreate) {
        return this.request<News>('/api/news', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async uploadNewsImage(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/news/upload-image`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<{ image_url: string }>;
    }

    async updateNews(id: string, data: NewsUpdate) {
        return this.request<News>(`/api/news/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteNews(id: string) {
        return this.request<void>(`/api/news/${id}`, {
            method: 'DELETE',
        });
    }

    async getAboutContent() {
        return this.request<{ content_en: string | null; content_pt: string | null }>('/api/about');
    }

    async updateAboutContent(data: { content_en?: string; content_pt?: string }) {
        return this.request<{ content_en: string | null; content_pt: string | null }>('/api/about', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // Email templates endpoints
    async getEmailTemplates() {
        return this.request<EmailTemplate[]>('/api/admin/email-templates');
    }

    async updateEmailTemplate(key: string, data: Partial<Pick<EmailTemplate, 'subject_en' | 'subject_pt' | 'body_en' | 'body_pt'>>) {
        return this.request<EmailTemplate>(`/api/admin/email-templates/${key}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // Hero slides endpoints
    async getSlides() {
        return this.request<HeroSlide[]>('/api/slides');
    }

    async createSlide(data: HeroSlideCreate) {
        return this.request<HeroSlide>('/api/slides', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateSlide(id: number, data: HeroSlideUpdate) {
        return this.request<HeroSlide>(`/api/slides/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteSlide(id: number) {
        return this.request<void>(`/api/slides/${id}`, {
            method: 'DELETE',
        });
    }

    async uploadSlideImage(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/slides/upload-image`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<{ image_url: string }>;
    }

    async reorderSlides(order: { id: number; order: number }[]) {
        return this.request<void>('/api/slides/reorder', {
            method: 'PUT',
            body: JSON.stringify(order),
        });
    }

    // Hero logos endpoints
    async getLogos() {
        return this.request<HeroLogo[]>('/api/logos');
    }

    async uploadLogo(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/logos/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<HeroLogo>;
    }

    async replaceLogo(id: number, file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/logos/${id}/replace`, {
            method: 'PUT',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json() as Promise<HeroLogo>;
    }

    async deleteLogo(id: number) {
        return this.request<void>(`/api/logos/${id}`, {
            method: 'DELETE',
        });
    }

    async reorderLogos(order: { id: number; order: number }[]) {
        return this.request<void>('/api/logos/reorder', {
            method: 'PUT',
            body: JSON.stringify(order),
        });
    }

    async getLogoConfig() {
        return this.request<HeroLogoConfig>('/api/logos/config');
    }

    async updateLogoConfig(data: Partial<HeroLogoConfig>) {
        return this.request<HeroLogoConfig>('/api/logos/config', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // Site config (header logo + favicon)
    async getSiteConfig() {
        return this.request<SiteConfig>('/api/site-config');
    }

    async updateSiteConfig(data: { header_logo_size?: string; header_text?: string; footer_address?: string; footer_phone?: string; footer_email?: string }) {
        return this.request<SiteConfig>('/api/site-config', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async uploadHeaderLogo(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${this.baseUrl}/api/site-config/upload-header-logo`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }
        return response.json() as Promise<SiteConfig>;
    }

    // Admin scores endpoints
    async getAdminScores(cluster: string) {
        return this.request<AdminScoreCategory[]>(`/api/admin/scores/${cluster}`);
    }

    async updateAdminScores(cluster: string, updates: AdminStructureUpdate[]) {
        return this.request<{ updated: number }>(`/api/admin/scores/${cluster}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }

    // Characterization options admin endpoints
    async getAdminCharacterization(): Promise<DropdownOptions> {
        return this.request<DropdownOptions>('/api/admin/characterization');
    }

    async updateAdminCharacterization(field: string, items: DropdownOptionItem[], fieldLabelEn?: string, fieldLabelPt?: string): Promise<void> {
        return this.request<void>(`/api/admin/characterization/${field}`, {
            method: 'PUT',
            body: JSON.stringify({ options: items, field_label_en: fieldLabelEn, field_label_pt: fieldLabelPt }),
        });
    }

    async uploadFavicon(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${this.baseUrl}/api/site-config/upload-favicon`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }
        return response.json() as Promise<SiteConfig>;
    }

    async deleteHeaderLogo() {
        return this.request<SiteConfig>('/api/site-config/header-logo', { method: 'DELETE' });
    }

    async deleteFavicon() {
        return this.request<SiteConfig>('/api/site-config/favicon', { method: 'DELETE' });
    }
}


export const api = new ApiClient(API_URL);

